/**
 * Layout principale della shell: sidebar + header con tab modulo + area contenuto.
 *
 * Header: 48px, sfondo #1B3A6B, logo "Floraltech ERP", label gruppo,
 * tab modulo attivo, menu utente con toggle tema e logout.
 *
 * Il tema viene dal ThemeContext (provider in main.tsx).
 * L'utente corrente viene dal AuthContext (provider in main.tsx).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Building2, ChevronDown, LogOut, Moon, Sun, User } from 'lucide-react';
import { useUO } from './UOContext';
import NotificheBell from './NotificheBell';
import Sidebar from './Sidebar';
import { useTheme, type Theme } from './ThemeContext';
import { useAuth } from './AuthContext';
import { registeredModules } from '../registry/modules';
import { sidebarGroups } from '../registry/groups';

const Layout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { resolved, setTheme } = useTheme();
  const { user, logout } = useAuth();
  const { currentUO, availableUOs, switchUO } = useUO();

  /* ─── Dropdown selettore ramo ─── */
  const [uoMenuOpen, setUoMenuOpen] = useState(false);
  const uoMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!uoMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (uoMenuRef.current && !uoMenuRef.current.contains(e.target as Node)) {
        setUoMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [uoMenuOpen]);

  /* ─── Menu utente dropdown ─── */
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  /** Chiude il menu al click fuori. */
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  /** Alterna tra light e dark (system non ciclato dal bottone header). */
  const toggleTheme = useCallback(() => {
    const next: Theme = resolved === 'dark' ? 'light' : 'dark';
    setTheme(next);
  }, [resolved, setTheme]);

  /** Trova il gruppo e modulo attivo in base all'URL corrente. */
  const activeModule = useMemo(() => {
    for (const group of sidebarGroups) {
      for (const moduloNome of group.moduli) {
        const mod = registeredModules.find((m) => m.nome === moduloNome);
        if (!mod) continue;
        for (const route of mod.routes) {
          const path = route.path;
          if (
            location.pathname === path ||
            (!path.includes(':') && location.pathname.startsWith(path + '/')) ||
            (path.includes(':') && location.pathname.startsWith(path.split(':')[0]))
          ) {
            return { group, mod };
          }
        }
      }
    }
    return null;
  }, [location.pathname]);

  /** Tab del modulo attivo da mostrare nell'header. */
  const moduleTabs = useMemo(() => {
    if (!activeModule) return [];
    return activeModule.mod.routes.filter((r) => !r.hidden && r.label);
  }, [activeModule]);

  /** Iniziali dell'utente per l'avatar. */
  const initials = user
    ? `${(user.nome?.[0] ?? '').toUpperCase()}${(user.cognome?.[0] ?? '').toUpperCase()}`
    : '?';

  /** Avatar utente (immagine) scaricata come blob autenticato, se presente. */
  const [avatarSrc, setAvatarSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!user?.avatar_documento_id) {
      setAvatarSrc(null);
      return;
    }
    let revoked = false;
    let createdUrl: string | null = null;
    const token = localStorage.getItem('token');
    fetch(`/api/v1/utenti/${user.id}/avatar`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(async (res) => {
        if (!res.ok) throw new Error('no avatar');
        const blob = await res.blob();
        if (revoked) return;
        createdUrl = URL.createObjectURL(blob);
        setAvatarSrc(createdUrl);
      })
      .catch(() => {
        if (!revoked) setAvatarSrc(null);
      });
    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [user?.avatar_documento_id, user?.id]);

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        {/* ═══ HEADER 48px ═══ */}
        <header
          className="flex flex-shrink-0 items-center gap-4 px-4"
          style={{ height: 48, backgroundColor: '#1B3A6B' }}
        >
          {/* Logo */}
          <span
            className="font-outfit whitespace-nowrap text-base text-white"
            style={{ fontWeight: 500 }}
          >
            Floraltech ERP
          </span>

          {/* Selettore ramo operativo */}
          {availableUOs.length > 0 && (
            <>
              <div className="h-5 w-px" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }} />
              <div ref={uoMenuRef} style={{ position: 'relative' }}>
                {availableUOs.length === 1 ? (
                  /* Un solo ramo: testo statico */
                  <span
                    className="flex items-center gap-1.5"
                    style={{ color: '#A8BDD8', fontSize: 12, fontWeight: 500 }}
                  >
                    <Building2 size={14} />
                    {availableUOs[0].nome}
                  </span>
                ) : (
                  /* Più rami: dropdown */
                  <>
                    <button
                      onClick={() => setUoMenuOpen((prev) => !prev)}
                      className="flex items-center gap-1.5 transition-colors duration-150"
                      style={{
                        height: 28,
                        padding: '0 8px',
                        borderRadius: 5,
                        backgroundColor: uoMenuOpen ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        color: '#FFFFFF',
                        fontSize: 12,
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      <Building2 size={14} />
                      {currentUO?.nome ?? 'Seleziona ramo'}
                      <ChevronDown size={12} />
                    </button>
                    {uoMenuOpen && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 34,
                          left: 0,
                          minWidth: 200,
                          backgroundColor: 'var(--color-card)',
                          border: '1px solid var(--color-border)',
                          borderRadius: 8,
                          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                          zIndex: 50,
                          overflow: 'hidden',
                          padding: '4px 0',
                        }}
                      >
                        {availableUOs.map((uo) => (
                          <button
                            key={uo.id}
                            onClick={async () => {
                              setUoMenuOpen(false);
                              await switchUO(uo.id);
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              width: '100%',
                              padding: '8px 16px',
                              fontSize: 13,
                              border: 'none',
                              cursor: 'pointer',
                              backgroundColor: uo.id === currentUO?.id ? 'var(--color-muted)' : 'transparent',
                              color: 'var(--color-foreground)',
                              fontWeight: uo.id === currentUO?.id ? 600 : 400,
                            }}
                          >
                            <Building2 size={14} />
                            <span style={{ flex: 1, textAlign: 'left' }}>{uo.nome}</span>
                            <span
                              style={{
                                fontSize: 11,
                                color: 'var(--color-muted-foreground)',
                              }}
                            >
                              {uo.ruolo}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {/* Separatore verticale */}
          <div className="h-5 w-px" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }} />

          {/* Label gruppo attivo */}
          {activeModule && (
            <span
              className="whitespace-nowrap"
              style={{
                color: '#A8BDD8',
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {activeModule.group.label}
            </span>
          )}

          {/* Tab modulo attivo */}
          <nav className="flex flex-1 items-center gap-1 overflow-x-auto">
            {moduleTabs.map((tab) => {
              const isActive =
                location.pathname === tab.path ||
                (!tab.path.includes(':') && location.pathname.startsWith(tab.path + '/'));

              return (
                <NavLink
                  key={tab.path}
                  to={tab.path}
                  className="relative flex-shrink-0 px-3 text-sm transition-colors duration-150"
                  style={{
                    color: isActive ? '#FFFFFF' : '#A8BDD8',
                    fontWeight: isActive ? 600 : 400,
                    lineHeight: '48px',
                  }}
                >
                  {tab.label}
                  {isActive && (
                    <span
                      className="absolute bottom-0 left-0 right-0"
                      style={{
                        height: 2,
                        backgroundColor: '#C9952A',
                        borderRadius: 0,
                      }}
                    />
                  )}
                </NavLink>
              );
            })}
          </nav>

          {/* Campanella notifiche */}
          <NotificheBell />

          {/* ═══ MENU UTENTE ═══ */}
          <div ref={menuRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setMenuOpen((prev) => !prev)}
              className="flex items-center gap-2 transition-colors duration-150"
              style={{
                height: 32,
                paddingLeft: 2,
                paddingRight: 10,
                borderRadius: 6,
                backgroundColor: menuOpen ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.094)',
                border: '1px solid rgba(255,255,255,0.267)',
                color: '#FFFFFF',
                cursor: 'pointer',
              }}
              title="Menu utente"
            >
              {/* Avatar iniziali */}
              <span
                className="flex items-center justify-center"
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  backgroundColor: '#C9952A',
                  color: '#FFFFFF',
                  fontSize: 11,
                  fontWeight: 600,
                  lineHeight: 1,
                  overflow: 'hidden',
                }}
              >
                {avatarSrc ? (
                  <img src={avatarSrc} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  initials
                )}
              </span>
              {user && (
                <span style={{ fontSize: 13, fontWeight: 400, whiteSpace: 'nowrap' }}>
                  {user.nome}
                </span>
              )}
            </button>

            {/* Dropdown */}
            {menuOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: 38,
                  right: 0,
                  minWidth: 220,
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 8,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  zIndex: 50,
                  overflow: 'hidden',
                }}
              >
                {/* Info utente */}
                {user && (
                  <div
                    style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid var(--color-border)',
                    }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-foreground)' }}>
                      {user.nome} {user.cognome}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-muted-foreground)', marginTop: 2 }}>
                      {user.email}
                    </div>
                    <div
                      style={{
                        display: 'inline-block',
                        marginTop: 6,
                        padding: '2px 8px',
                        fontSize: 11,
                        fontWeight: 500,
                        borderRadius: 4,
                        backgroundColor: 'rgba(27,58,107,0.1)',
                        color: '#1B3A6B',
                      }}
                    >
                      {user.ruolo}
                    </div>
                  </div>
                )}

                {/* Voci menu */}
                <div style={{ padding: '4px 0' }}>
                  {/* Il mio profilo */}
                  <MenuItemButton
                    icon={<User size={15} />}
                    label="Il mio profilo"
                    onClick={() => {
                      setMenuOpen(false);
                      navigate('/profilo');
                    }}
                  />

                  {/* Toggle tema */}
                  <MenuItemButton
                    icon={resolved === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
                    label={resolved === 'dark' ? 'Tema chiaro' : 'Tema scuro'}
                    onClick={() => {
                      toggleTheme();
                      setMenuOpen(false);
                    }}
                  />
                </div>

                {/* Esci */}
                <div style={{ borderTop: '1px solid var(--color-border)', padding: '4px 0' }}>
                  <MenuItemButton
                    icon={<LogOut size={15} />}
                    label="Esci"
                    danger
                    onClick={() => {
                      setMenuOpen(false);
                      logout();
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </header>

        {/* ═══ CONTENUTO ═══ */}
        <main
          className="flex-1 overflow-y-auto"
          style={{
            backgroundColor: 'var(--color-background)',
            padding: 24,
          }}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
};

/* ─── Componente interno: voce del dropdown ─── */

interface MenuItemButtonProps {
  icon: React.ReactNode;
  label: string;
  danger?: boolean;
  onClick: () => void;
}

const MenuItemButton: React.FC<MenuItemButtonProps> = ({ icon, label, danger, onClick }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        padding: '8px 16px',
        fontSize: 13,
        border: 'none',
        cursor: 'pointer',
        backgroundColor: hovered
          ? danger
            ? 'rgba(220,38,38,0.08)'
            : 'var(--color-muted, rgba(0,0,0,0.04))'
          : 'transparent',
        color: danger
          ? 'var(--color-destructive, #DC2626)'
          : 'var(--color-foreground)',
        transition: 'background-color 150ms',
      }}
    >
      {icon}
      {label}
    </button>
  );
};

export default Layout;
